import {
  ApolloClient,
  ApolloLink,
  createHttpLink,
  InMemoryCache,
  NormalizedCacheObject,
} from "@apollo/client";
import {GetServerSidePropsContext, NextPageContext} from "next";
import {useMemo} from "react";
import {setContext} from "@apollo/client/link/context";
import { onError } from "@apollo/client/link/error";
import Router from "next/router";
import { getLoginUrlAfterAuthFailure, isPublicPath } from "@/lib/redirect";

let apolloClient: ApolloClient<NormalizedCacheObject>;

export const APOLLO_URI = process.env.NEXT_PUBLIC_APOLLO_URI || "/api/graphql/";
export const APOLLO_BACKEND_URI = process.env.APOLLO_BACKEND_URI || APOLLO_URI

export const createApolloClient = (
    link: ApolloLink,
) => {
    return new ApolloClient({
        ssrMode: !process.browser,
        cache: new InMemoryCache(),
        link,
        connectToDevTools: process.env.NODE_ENV !== "production",
    });
};

export const initializeApollo = ({
                                     initialState = undefined,
                                     ctx = undefined,
                                 }: {
    initialState?: NormalizedCacheObject;
    ctx?: NextPageContext | GetServerSidePropsContext;
}, link?: ApolloLink) => {
    const _apolloClient = apolloClient ?? createApolloClient(link || createApolloHTTPLink("", ctx))

    if (initialState) {
        const existingCache = _apolloClient.extract();
        if(initialState['ROOT_QUERY']?.['myProfile'] === null) {
            delete initialState['ROOT_QUERY']['myProfile'];
        }
        _apolloClient.cache.restore({...existingCache, ...initialState});
    }

    if (process.browser && !apolloClient) apolloClient = _apolloClient;
    return _apolloClient;
};


export const useApollo = (initialState: NormalizedCacheObject) => {
    return useMemo(() => initializeApollo({initialState}, createApolloHTTPLink(APOLLO_URI)), [initialState]);
}

function isUnauthenticatedApolloError(args: {
  graphQLErrors?: readonly any[];
  networkError?: any;
}): boolean {
  const { graphQLErrors, networkError } = args;

  const statusCode = (networkError as any)?.statusCode ?? (networkError as any)?.status;
  if (statusCode === 401) return true;

  // Graphene commonly returns HTTP 200 with GraphQL errors on auth failure.
  const errors = Array.isArray(graphQLErrors) ? graphQLErrors : [];
  return errors.some((e) => {
    const code = e?.extensions?.code;
    if (code === "UNAUTHENTICATED") return true;
    const msg = String(e?.message || "").toLowerCase();
    return (
      msg.includes("signature has expired") ||
      msg.includes("invalid token") ||
      msg.includes("not authenticated") ||
      msg.includes("not logged in") ||
      msg.includes("authentication credentials") ||
      msg.includes("login required")
    );
  });
}

function redirectToLoginOnce() {
  if (typeof window === "undefined") return;
  if ((window as any).__pdsAuthRedirecting) return;
  (window as any).__pdsAuthRedirecting = true;

  // Best-effort: clear cached data so we don't render stale UI while redirecting.
  try {
    apolloClient?.clearStore();
  } catch {
    // ignore
  }

  const path = window.location.pathname || "/";
  // Auth and public pages may issue profile queries without requiring a login.
  if (path.startsWith("/auth/") || isPublicPath(path)) return;

  const currentPath = `${path}${window.location.search || ""}`;
  Router.replace(getLoginUrlAfterAuthFailure(currentPath));
}

export function createApolloHTTPLink(uri='', ctx?: NextPageContext | GetServerSidePropsContext) {
    const httpLink = createHttpLink({
        uri: uri || APOLLO_BACKEND_URI,
        credentials: "include",
        fetch,
    });
    const authLink = setContext((_, {headers}) => {
        // get the authentication token from local storage if it exists
        // return the headers to the context so httpLink can read them
        return {
            headers: {
                ...headers,
                cookie: ctx?.req?.headers?.cookie,
            }
        }
    });
    const errorLink = onError(({ graphQLErrors, networkError }) => {
      if (isUnauthenticatedApolloError({ graphQLErrors, networkError })) {
        redirectToLoginOnce();
      }
    });
    return ApolloLink.from([errorLink, authLink.concat(httpLink)]);
}
